import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Chip,
  Stack,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { DialogTextActionButton } from "../../Base/DialogTextActionButton";
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  TableRestaurant as TableIcon,
  MeetingRoom as RoomIcon,
  GridOn as GridIcon,
  Settings as SettingsIcon,
  SwapHoriz as SwapIcon,
} from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../../store";
import { fetchTables } from "../../../features/matchs/matchsSlice";
import { Table } from "../../../types/Table";
import firestoreInstance from "../../../services/firestoreInstance";
import {
  doc,
  setDoc,
  deleteDoc,
  collection,
  getDocs,
} from "firebase/firestore";

interface TableConfig {
  tableId: number;
  roomId: number;
  roomName: string;
  row: number;
  column: number;
}

interface RoomConfig {
  roomId: number;
  name: string;
  rows: number;
  columns: number;
  numberOfTables: number;
  serpentMode: boolean;
}

interface TablePosition {
  tableId: number;
  row: number;
  column: number;
}

const TablesManagement: React.FC = () => {
  const dispatch = useAppDispatch();
  const firebaseTables = useAppSelector((state) => state.matchs.tables);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  // Configuration des salles
  const [rooms, setRooms] = useState<RoomConfig[]>([
    {
      roomId: 1,
      name: "Salle 1",
      rows: 2,
      columns: 4,
      numberOfTables: 6,
      serpentMode: true,
    },
  ]);

  // Positions des tables pour chaque salle (pour le mode libre)
  const [tablePositions, setTablePositions] = useState<{
    [roomId: number]: TablePosition[];
  }>({});

  // Ordre inversé des tables par salle (pour le mode serpent)
  const [invertedTableOrder, setInvertedTableOrder] = useState<{
    [roomId: number]: boolean;
  }>({});

  // Dialog pour ajouter/modifier une salle
  const [roomDialog, setRoomDialog] = useState<{
    open: boolean;
    room: RoomConfig | null;
    isEdit: boolean;
  }>({ open: false, room: null, isEdit: false });

  // État pour suivre les modifications non sauvegardées
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const loadTables = useCallback(async () => {
    setLoading(true);
    try {
      await dispatch(fetchTables());
      setMessage({ type: "success", text: "Tables chargées avec succès" });
    } catch (error) {
      setMessage({
        type: "error",
        text: "Erreur lors du chargement des tables",
      });
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    loadTables();
  }, [loadTables]);

  // Analyser les tables chargées et créer les salles correspondantes
  useEffect(() => {
    if (firebaseTables.length > 0) {
      // Grouper les tables par roomId
      const tablesByRoom: { [roomId: number]: Table[] } = {};
      firebaseTables.forEach((table) => {
        if (!tablesByRoom[table.roomId]) {
          tablesByRoom[table.roomId] = [];
        }
        tablesByRoom[table.roomId].push(table);
      });

      // Créer les configurations de salles à partir des tables existantes
      const inferredRooms: RoomConfig[] = Object.entries(tablesByRoom).map(
        ([roomIdStr, roomTables]) => {
          const roomId = parseInt(roomIdStr);

          // Déduire les dimensions de la salle à partir des positions des tables
          const maxRow = Math.max(...roomTables.map((t) => t.row));
          const maxColumn = Math.max(...roomTables.map((t) => t.column));

          // Déterminer si c'est un mode serpent en analysant la disposition
          const isSerpentMode = inferSerpentMode(roomTables, maxRow, maxColumn);

          // Utiliser le roomName de la première table si disponible, sinon nom par défaut
          const roomName = roomTables[0]?.roomName || `Salle ${roomId}`;

          return {
            roomId,
            name: roomName,
            rows: maxRow,
            columns: maxColumn,
            numberOfTables: roomTables.length,
            serpentMode: isSerpentMode,
          };
        }
      );

      // Fusionner les salles inférées avec les salles locales existantes
      // pour préserver les modifications locales non sauvegardées
      const mergedRooms = inferredRooms.map((inferredRoom) => {
        const existingRoom = rooms.find(
          (r) => r.roomId === inferredRoom.roomId
        );
        if (existingRoom) {
          // Déterminer quel nom utiliser
          const defaultName = `Salle ${inferredRoom.roomId}`;
          const shouldUseFirebaseName = inferredRoom.name !== defaultName;
          const shouldUseLocalName = existingRoom.name !== defaultName;

          let finalName = existingRoom.name; // Par défaut, préserver le nom local

          if (shouldUseFirebaseName && !shouldUseLocalName) {
            finalName = inferredRoom.name;
          } else if (shouldUseLocalName) {
            finalName = existingRoom.name;
          }

          // Préserver les propriétés locales (serpentMode, etc.) si elles existent
          return {
            ...inferredRoom,
            name: finalName,
            serpentMode: existingRoom.serpentMode, // Préserver le mode serpent local
            rows: existingRoom.rows, // Préserver les dimensions locales
            columns: existingRoom.columns,
            numberOfTables: existingRoom.numberOfTables,
          };
        }
        return inferredRoom;
      });

      // Ajouter les salles locales qui n'existent pas encore dans Firebase
      const localOnlyRooms = rooms.filter(
        (localRoom) =>
          !inferredRooms.some(
            (inferredRoom) => inferredRoom.roomId === localRoom.roomId
          )
      );
      mergedRooms.push(...localOnlyRooms);

      // Mettre à jour les salles seulement si elles sont différentes
      const currentRoomsString = JSON.stringify(rooms);
      const mergedRoomsString = JSON.stringify(mergedRooms);

      if (currentRoomsString !== mergedRoomsString) {
        setRooms(mergedRooms);
      }

      // Initialiser les positions pour les salles en mode libre
      const newPositionsFromTables: { [roomId: number]: TablePosition[] } = {};
      mergedRooms.forEach((room) => {
        if (!room.serpentMode) {
          const roomTables = tablesByRoom[room.roomId];
          if (roomTables) {
            const positions = roomTables.map((table) => ({
              tableId: table.tableId,
              row: table.row,
              column: table.column,
            }));
            newPositionsFromTables[room.roomId] = positions;
          }
        }
      });

      if (Object.keys(newPositionsFromTables).length > 0) {
        setTablePositions((prev) => ({
          ...prev,
          ...newPositionsFromTables,
        }));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- rooms exclu volontairement pour éviter la boucle
  }, [firebaseTables]);

  // Fonction pour déduire si une salle est en mode serpent
  const inferSerpentMode = (
    roomTables: Table[],
    maxRow: number,
    maxColumn: number
  ): boolean => {
    // Si toutes les positions sont remplies de manière séquentielle, c'est probablement un mode serpent
    const expectedPositions = maxRow * maxColumn;
    const actualTables = roomTables.length;

    // Si le nombre de tables est égal au nombre de positions, c'est probablement un mode serpent
    if (actualTables === expectedPositions) {
      return true;
    }

    // Vérifier si les tables sont disposées de manière séquentielle (gauche à droite, haut en bas)
    const sortedTables = roomTables.sort((a, b) => {
      if (a.row !== b.row) return a.row - b.row;
      return a.column - b.column;
    });

    let isSequential = true;
    for (let i = 0; i < sortedTables.length; i++) {
      const table = sortedTables[i];
      const expectedRow = Math.floor(i / maxColumn) + 1;
      const expectedColumn = (i % maxColumn) + 1;

      if (table.row !== expectedRow || table.column !== expectedColumn) {
        isSequential = false;
        break;
      }
    }

    return isSequential;
  };

  // Initialiser les positions pour les salles en mode libre (pour les nouvelles salles ajoutées manuellement)
  useEffect(() => {
    const newPositions: { [roomId: number]: TablePosition[] } = {};

    rooms.forEach((room) => {
      if (!room.serpentMode) {
        const existingPositions = tablePositions[room.roomId];

        if (!existingPositions || existingPositions.length === 0) {
          // Créer des positions à partir des tables Firebase existantes
          const roomFirebaseTables = firebaseTables.filter(
            (table) => table.roomId === room.roomId
          );

          if (roomFirebaseTables.length > 0) {
            // Utiliser les positions des tables Firebase
            const positions = roomFirebaseTables.map((table) => ({
              tableId: table.tableId,
              row: table.row,
              column: table.column,
            }));
            newPositions[room.roomId] = positions;
          } else {
            // Créer des positions par défaut si pas de tables Firebase
            const defaultPositions: TablePosition[] = [];
            let tableId = 1;

            for (
              let row = 1;
              row <= room.rows && tableId <= room.numberOfTables;
              row++
            ) {
              for (
                let col = 1;
                col <= room.columns && tableId <= room.numberOfTables;
                col++
              ) {
                defaultPositions.push({
                  tableId: tableId++,
                  row: row,
                  column: col,
                });
              }
            }

            newPositions[room.roomId] = defaultPositions;
          }
        } else {
          newPositions[room.roomId] = existingPositions;
        }
      }
    });

    if (Object.keys(newPositions).length > 0) {
      setTablePositions((prev) => {
        const hasChanges = Object.keys(newPositions).some((roomId) => {
          const roomIdNum = parseInt(roomId);
          const prevPositions = prev[roomIdNum];
          const newPositionsForRoom = newPositions[roomIdNum];
          return (
            JSON.stringify(prevPositions) !==
            JSON.stringify(newPositionsForRoom)
          );
        });

        if (!hasChanges) {
          return prev;
        }

        return {
          ...prev,
          ...newPositions,
        };
      });
    }
  }, [rooms, firebaseTables, tablePositions]);

  // Helper pour calculer l'offset de tableId pour chaque salle
  const getTableIdOffset = (roomId: number) => {
    let offset = 0;
    for (const room of rooms) {
      if (room.roomId === roomId) break;
      offset += room.numberOfTables;
    }
    return offset;
  };

  const generateTablesForRoom = (
    room: RoomConfig,
    offset: number
  ): TableConfig[] => {
    // Générer les tables localement (toujours disposition automatique)
    let generatedTables: TableConfig[] = [];
    let tableId = 1 + offset;

    // Forcer le nombre de tables à rows * columns si inférieur (sauf si l'utilisateur a choisi moins)
    let numberOfTables = room.numberOfTables;
    const maxTables = room.rows * room.columns;
    if (numberOfTables > maxTables) {
      numberOfTables = maxTables;
    }

    // Disposition automatique en zigzag (serpent)
    const positions: { row: number; column: number }[] = [];
    for (let row = 1; row <= room.rows; row++) {
      for (let col = 1; col <= room.columns; col++) {
        positions.push({ row, column: col });
      }
    }
    // Inverser l'ordre des colonnes pour les rangées paires (serpent classique)
    for (let row = 1; row <= room.rows; row++) {
      if (row % 2 === 0) {
        const rowPositions = positions.filter((p) => p.row === row);
        rowPositions.reverse();
        positions.splice(
          positions.findIndex((p) => p.row === row),
          rowPositions.length,
          ...rowPositions
        );
      }
    }
    // Prendre seulement le nombre de tables demandé
    const selectedPositions = positions.slice(0, numberOfTables);
    selectedPositions.forEach((pos) => {
      generatedTables.push({
        tableId: tableId++,
        roomId: room.roomId,
        roomName: room.name,
        row: pos.row,
        column: pos.column,
      });
    });
    // Si l'ordre est inversé, inverser les tableId
    if (invertedTableOrder[room.roomId]) {
      const maxTableId = generatedTables.length;
      generatedTables = generatedTables.map((table, index) => ({
        ...table,
        tableId: maxTableId - index,
      }));
    }
    return generatedTables;
  };

  const saveTablesToFirestore = async (tableConfigs: TableConfig[]) => {
    setLoading(true);
    try {
      // Supprimer toutes les tables existantes
      const existingTables = await getDocs(
        collection(firestoreInstance, "table")
      );
      const deletePromises = existingTables.docs.map((doc) =>
        deleteDoc(doc.ref)
      );
      await Promise.all(deletePromises);

      // Ajouter les nouvelles tables
      const addPromises = tableConfigs.map((tableConfig) =>
        setDoc(
          doc(firestoreInstance, "table", tableConfig.tableId.toString()),
          tableConfig
        )
      );
      await Promise.all(addPromises);

      // Recharger les tables
      await dispatch(fetchTables());

      setMessage({ type: "success", text: "Tables sauvegardées avec succès" });
    } catch (error) {
      console.error("Erreur lors de la sauvegarde:", error);
      setMessage({
        type: "error",
        text: "Erreur lors de la sauvegarde des tables",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTables = async () => {
    const allTables: TableConfig[] = [];
    rooms.forEach((room) => {
      const offset = getTableIdOffset(room.roomId);
      const roomTables = generateTablesForRoom(room, offset);
      allTables.push(...roomTables);
    });
    await saveTablesToFirestore(allTables);
    setHasUnsavedChanges(false); // Réinitialiser après sauvegarde
  };

  const handleAddRoom = () => {
    const newRoomId = Math.max(...rooms.map((r) => r.roomId), 0) + 1;
    setRoomDialog({
      open: true,
      room: {
        roomId: newRoomId,
        name: `Salle ${newRoomId}`,
        rows: 2,
        columns: 4,
        numberOfTables: 8,
        serpentMode: true,
      },
      isEdit: false,
    });
  };

  const handleEditRoom = (room: RoomConfig) => {
    setRoomDialog({
      open: true,
      room: { ...room },
      isEdit: true,
    });
  };

  const handleDeleteRoom = (roomId: number) => {
    setRooms(rooms.filter((r) => r.roomId !== roomId));
    // Supprimer aussi les positions sauvegardées
    const newPositions = { ...tablePositions };
    delete newPositions[roomId];
    setTablePositions(newPositions);
  };

  const handleInvertTableOrder = (roomId: number) => {
    setInvertedTableOrder((prev) => ({
      ...prev,
      [roomId]: !prev[roomId],
    }));
  };

  const handleSaveRoom = () => {
    if (!roomDialog.room) return;

    if (roomDialog.isEdit) {
      setRooms(
        rooms.map((r) =>
          r.roomId === roomDialog.room!.roomId ? roomDialog.room! : r
        )
      );
      setHasUnsavedChanges(true); // Marquer comme modifié

      // Initialiser les positions si la salle passe en mode libre
      if (!roomDialog.room.serpentMode) {
        const existingPositions = tablePositions[roomDialog.room.roomId];

        if (!existingPositions || existingPositions.length === 0) {
          // Créer des positions à partir des tables Firebase existantes
          const roomFirebaseTables = firebaseTables.filter(
            (table) => table.roomId === roomDialog.room!.roomId
          );

          if (roomFirebaseTables.length > 0) {
            // Utiliser les positions des tables Firebase
            const positions = roomFirebaseTables.map((table) => ({
              tableId: table.tableId,
              row: table.row,
              column: table.column,
            }));

            setTablePositions((prev) => ({
              ...prev,
              [roomDialog.room!.roomId]: positions,
            }));
          } else {
            // Créer des positions par défaut si pas de tables Firebase
            const defaultPositions: TablePosition[] = [];
            let tableId = 1;

            for (
              let row = 1;
              row <= roomDialog.room.rows &&
              tableId <= roomDialog.room.numberOfTables;
              row++
            ) {
              for (
                let col = 1;
                col <= roomDialog.room.columns &&
                tableId <= roomDialog.room.numberOfTables;
                col++
              ) {
                defaultPositions.push({
                  tableId: tableId++,
                  row: row,
                  column: col,
                });
              }
            }

            setTablePositions((prev) => ({
              ...prev,
              [roomDialog.room!.roomId]: defaultPositions,
            }));
          }
        }
      }
    } else {
      setRooms([...rooms, roomDialog.room]);
      setHasUnsavedChanges(true); // Marquer comme modifié

      // Initialiser les positions pour la nouvelle salle si elle est en mode libre
      if (!roomDialog.room.serpentMode) {
        const defaultPositions: TablePosition[] = [];
        let tableId = 1;

        for (
          let row = 1;
          row <= roomDialog.room.rows &&
          tableId <= roomDialog.room.numberOfTables;
          row++
        ) {
          for (
            let col = 1;
            col <= roomDialog.room.columns &&
            tableId <= roomDialog.room.numberOfTables;
            col++
          ) {
            defaultPositions.push({
              tableId: tableId++,
              row: row,
              column: col,
            });
          }
        }

        setTablePositions((prev) => ({
          ...prev,
          [roomDialog.room!.roomId]: defaultPositions,
        }));
      }
    }

    setRoomDialog({ open: false, room: null, isEdit: false });
  };

  const renderTablePreview = (room: RoomConfig) => {
    const offset = getTableIdOffset(room.roomId);
    const tables = generateTablesForRoom(room, offset);

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Aperçu de la disposition ({tables.length} tables sur {room.rows}×
          {room.columns} = {room.rows * room.columns} positions):
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50" }}>
          <Grid container spacing={1}>
            {Array.from({ length: room.rows }, (_, row) => (
              <Grid container key={row} spacing={1} sx={{ mb: 1 }}>
                {Array.from({ length: room.columns }, (_, col) => {
                  const table = tables.find(
                    (t) => t.row === row + 1 && t.column === col + 1
                  );

                  return (
                    <Grid item key={col}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          border: "1px solid #ccc",
                          borderRadius: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: table ? "primary.main" : "grey.200",
                          color: table ? "white" : "text.secondary",
                          fontSize: "0.75rem",
                          fontWeight: "bold",
                          cursor: "default",
                          "&:hover": {
                            bgcolor:
                              table && !room.serpentMode
                                ? "primary.dark"
                                : undefined,
                          },
                        }}
                        draggable={table && !room.serpentMode}
                      >
                        {table ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            {table.tableId}
                          </Box>
                        ) : (
                          ""
                        )}
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        sx={{ display: "flex", alignItems: "center", gap: 2 }}
      >
        <TableIcon color="primary" />
        Gestion des tables
        {hasUnsavedChanges && (
          <Chip
            label="Modifications non sauvegardées"
            color="warning"
            size="small"
            icon={<SaveIcon />}
          />
        )}
      </Typography>

      {/* Message d'alerte */}
      {message && (
        <Alert
          severity={message.type}
          sx={{ mb: 3 }}
          onClose={() => setMessage(null)}
        >
          {message.text}
        </Alert>
      )}

      {/* Configuration des salles */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <RoomIcon />
              Configuration des salles
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddRoom}
              sx={{ ml: 2 }}
            >
              Ajouter une salle
            </Button>
          </Box>

          <Grid container spacing={3}>
            {rooms.map((room) => (
                <Grid
                  item
                  xs={12}
                  md={rooms.length === 1 ? 12 : 6}
                  key={room.roomId}
                >
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                      }}
                    >
                      <Typography variant="h6">{room.name}</Typography>
                      <Box>
                        <Tooltip title="Modifier">
                          <IconButton
                            size="small"
                            onClick={() => handleEditRoom(room)}
                          >
                            <SettingsIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Supprimer">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteRoom(room.roomId)}
                            disabled={
                              room.roomId ===
                              Math.min(...rooms.map((r) => r.roomId))
                            }
                          >
                            <RemoveIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                      <Chip label={`${room.rows} rangées`} size="small" />
                      <Chip label={`${room.columns} colonnes`} size="small" />
                      <Chip
                        label={`${room.numberOfTables} tables`}
                        size="small"
                        color="primary"
                      />
                    </Stack>

                    {renderTablePreview(room)}
                </Paper>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Actions */}
      <Card>
        <CardContent>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <GridIcon />
            Actions
          </Typography>

          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              startIcon={
                loading ? <CircularProgress size={20} /> : <SaveIcon />
              }
              onClick={handleSaveTables}
              disabled={loading}
              color={hasUnsavedChanges ? "warning" : "primary"}
            >
              {loading
                ? "Sauvegarde..."
                : hasUnsavedChanges
                ? "Sauvegarder les modifications"
                : "Sauvegarder les tables"}
            </Button>

            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadTables}
              disabled={loading}
            >
              Actualiser
            </Button>
          </Stack>

          {firebaseTables.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Tables actuelles ({firebaseTables.length}):
              </Typography>
              <Paper
                variant="outlined"
                sx={{ p: 2, maxHeight: 200, overflow: "auto" }}
              >
                <Grid container spacing={1}>
                  {firebaseTables.map((table) => (
                    <Grid item key={table.tableId}>
                      <Chip
                        label={`Table ${table.tableId} (S${table.roomId}, R${table.row}, C${table.column})`}
                        size="small"
                        variant="outlined"
                      />
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Dialog pour ajouter/modifier une salle */}
      <Dialog
        open={roomDialog.open}
        onClose={() =>
          setRoomDialog({ open: false, room: null, isEdit: false })
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {roomDialog.isEdit ? "Modifier la salle" : "Ajouter une salle"}
        </DialogTitle>
        <DialogContent>
          {roomDialog.room && (
            <Box sx={{ pt: 1 }}>
              <TextField
                fullWidth
                label="Nom de la salle (pour l'affichage)"
                value={roomDialog.room.name}
                onChange={(e) =>
                  setRoomDialog({
                    ...roomDialog,
                    room: { ...roomDialog.room!, name: e.target.value },
                  })
                }
                sx={{ mb: 2 }}
                helperText="Le nom est utilisé uniquement pour l'affichage. L'ID de salle reste numérique."
              />

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Rangées"
                    type="number"
                    value={roomDialog.room.rows || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      setRoomDialog({
                        ...roomDialog,
                        room: {
                          ...roomDialog.room!,
                          rows:
                            value === ""
                              ? 1
                              : Math.max(1, Math.min(10, parseInt(value) || 1)),
                        },
                      });
                    }}
                    inputProps={{ min: 1, max: 10 }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Colonnes"
                    type="number"
                    value={roomDialog.room.columns || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      setRoomDialog({
                        ...roomDialog,
                        room: {
                          ...roomDialog.room!,
                          columns:
                            value === ""
                              ? 1
                              : Math.max(1, Math.min(15, parseInt(value) || 1)),
                        },
                      });
                    }}
                    inputProps={{ min: 1, max: 15 }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Nombre de tables"
                    type="number"
                    value={roomDialog.room.numberOfTables || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      const maxTables =
                        roomDialog.room!.rows * roomDialog.room!.columns;
                      setRoomDialog({
                        ...roomDialog,
                        room: {
                          ...roomDialog.room!,
                          numberOfTables:
                            value === ""
                              ? 1
                              : Math.max(
                                  1,
                                  Math.min(maxTables, parseInt(value) || 1)
                                ),
                        },
                      });
                    }}
                    inputProps={{
                      min: 1,
                      max: roomDialog.room.rows * roomDialog.room.columns,
                    }}
                    helperText={`Max: ${
                      roomDialog.room.rows * roomDialog.room.columns
                    }`}
                  />
                </Grid>
              </Grid>

              {renderTablePreview({ ...roomDialog.room })}

              <Box sx={{ display: "flex", alignItems: "center", mt: 2, mb: 2 }}>
                <Tooltip title="Inverser l'ordre des tables">
                  <IconButton
                    size="small"
                    onClick={() =>
                      handleInvertTableOrder(roomDialog.room!.roomId)
                    }
                    color={
                      invertedTableOrder[roomDialog.room!.roomId]
                        ? "primary"
                        : "default"
                    }
                  >
                    <SwapIcon />
                  </IconButton>
                </Tooltip>
                <Typography variant="body2" sx={{ ml: 1 }}>
                  Inverser l'ordre des tables
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <DialogTextActionButton
            onClick={() =>
              setRoomDialog({ open: false, room: null, isEdit: false })
            }
          >
            Annuler
          </DialogTextActionButton>
          <Button onClick={handleSaveRoom} variant="contained">
            {roomDialog.isEdit ? "Modifier" : "Ajouter"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TablesManagement;
